import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useApp, useClinicCtx } from '@/app/state';
import { Button, Card, Field, Input, Modal, Pill, Textarea, cx } from '@/components/ui';
import { ageLabel, getPatientIndex } from '@/features/patients/repo';
import { setTokenStatus } from '@/features/queue/repo';
import type { PatientIndexRow } from '@/lib/db/schema';
import {
  allergyConflicts, computeQuantity, ensurePrescription, loadDiagnoses, loadNarrative, loadRxItems, loadVitals,
  openEncounter, saveDiagnoses, saveNarrative, saveRxItems, saveVitals, signEncounter,
  type DiagnosisRow, type Narrative, type RxItemRow, type VitalsInput,
} from '@/features/consult/repo';
import { SearchPicker, useDosagePhrases, useDrugSearch, useIcd10Search, type DrugHit, type Icd10Hit } from '@/features/consult/pickers';
import { PrescriptionPrint } from '@/features/consult/PrescriptionPrint';
import { IssueCertificate } from '@/features/documents/IssueCertificate';
import { CertificatePrint } from '@/features/documents/CertificatePrint';
import { documentsForEncounter, getDocument, countPrint, type DocumentRow } from '@/features/documents/repo';

const num = (v: string): number | null => (v.trim() === '' ? null : Number(v));

export function ConsultPage() {
  const { tokenId } = useParams();
  const { t, i18n } = useTranslation();
  const my = i18n.language === 'my';
  const ctx = useClinicCtx();
  const { clinic } = useApp();
  const nav = useNavigate();

  const [encounterId, setEncounterId] = useState<string | null>(null);
  const [rxId, setRxId] = useState<string | null>(null);
  const [patient, setPatient] = useState<PatientIndexRow | null>(null);
  const [meta, setMeta] = useState<{ patient_id: string; doctor_id: string; session_id: string | null; doctor_name: string | null; doctor_reg_no: string | null; status: string } | null>(null);
  const [n, setN] = useState<Narrative>({});
  const [v, setV] = useState<Record<keyof VitalsInput, string>>({} as Record<keyof VitalsInput, string>);
  const [dx, setDx] = useState<DiagnosisRow[]>([]);
  const [rx, setRx] = useState<RxItemRow[]>([]);
  const [followUp, setFollowUp] = useState('');
  const [allergies, setAllergies] = useState<string[]>([]);
  const [state, setState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [addingDx, setAddingDx] = useState(false);
  const [addingRx, setAddingRx] = useState<DrugHit | { free: string } | null>(null);
  const [printing, setPrinting] = useState(false);
  const [issuingDoc, setIssuingDoc] = useState(false);
  const [showDoc, setShowDoc] = useState<DocumentRow | null>(null);
  const [docs, setDocs] = useState<DocumentRow[]>([]);
  const [docVersion, setDocVersion] = useState(0);
  const icd10 = useIcd10Search();
  const drugs = useDrugSearch();

  // Load or open the encounter for this token.
  useEffect(() => {
    if (!tokenId) return;
    let alive = true;
    (async () => {
      const tok = await ctx.db.getOptional<{ patient_id: string; session_id: string; doctor_id: string; name_en: string | null; name_my: string | null; mmc_reg_no: string | null; status: string }>(
        `select t.patient_id, t.session_id, s.doctor_id, d.name_en, d.name_my, d.mmc_reg_no, t.status from tokens t join sessions s on s.id = t.session_id join doctors d on d.id = s.doctor_id where t.id = ?`, [tokenId]);
      if (!tok || !alive) return;
      const eid = await openEncounter(ctx, { patient_id: tok.patient_id, doctor_id: tok.doctor_id, session_id: tok.session_id, token_id: tokenId });
      const pid = await ensurePrescription(ctx, eid, tok.patient_id, tok.doctor_id);
      const enc = await ctx.db.getOptional<{ status: string; follow_up_on: string | null }>('select status, follow_up_on from encounters where id = ?', [eid]);
      const [narr, vit, dl, rl, pi] = await Promise.all([
        loadNarrative(ctx, eid), loadVitals(ctx, eid), loadDiagnoses(ctx, eid), loadRxItems(ctx, pid), getPatientIndex(ctx.db, tok.patient_id),
      ]);
      if (!alive) return;
      setEncounterId(eid); setRxId(pid); setPatient(pi);
      setMeta({ patient_id: tok.patient_id, doctor_id: tok.doctor_id, session_id: tok.session_id, doctor_name: my ? (tok.name_my ?? tok.name_en) : (tok.name_en ?? tok.name_my), doctor_reg_no: tok.mmc_reg_no, status: enc?.status ?? 'open' });
      setN(narr);
      setFollowUp(enc?.follow_up_on ?? '');
      if (vit) setV(Object.fromEntries(Object.entries(vit).map(([k, val]) => [k, val == null ? '' : String(val)])) as Record<keyof VitalsInput, string>);
      setDx(dl); setRx(rl);
      // Allergy list for the banner
      const rows = await ctx.db.getAll<{ id: string; enc: string }>('select id, enc from patient_allergies where patient_id = ? and active = 1', [tok.patient_id]);
      const names: string[] = [];
      for (const r of rows) { try { names.push((await ctx.crypto.decrypt<{ substance: string }>(r.id, r.enc)).substance); } catch { /* other key */ } }
      if (alive) setAllergies(names);
      if (tok.status === 'called' || tok.status === 'waiting') void setTokenStatus({ ...ctx, tokenPrefix: '' }, tokenId, 'in_consultation');
    })();
    return () => { alive = false; };
  }, [tokenId, ctx, my]);

  // Debounced autosave of narrative and vitals.
  const timer = useRef<number | null>(null);
  const scheduleSave = useCallback(() => {
    if (!encounterId || !meta) return;
    setState('saving');
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(async () => {
      await saveNarrative(ctx, encounterId, n, followUp || null);
      await saveVitals(ctx, encounterId, meta.patient_id, {
        systolic: num(v.systolic ?? ''), diastolic: num(v.diastolic ?? ''), pulse: num(v.pulse ?? ''), temp_c: num(v.temp_c ?? ''),
        spo2: num(v.spo2 ?? ''), resp_rate: num(v.resp_rate ?? ''), weight_kg: num(v.weight_kg ?? ''), height_cm: num(v.height_cm ?? ''),
        glucose_mg_dl: num(v.glucose_mg_dl ?? ''), notes: v.notes || null,
      });
      setState('saved');
    }, 700);
  }, [ctx, encounterId, meta, n, v, followUp]);
  useEffect(() => { scheduleSave(); }, [n, v, followUp, scheduleSave]);

  useEffect(() => {
    if (!encounterId) return;
    void documentsForEncounter(ctx, encounterId).then(setDocs);
  }, [ctx, encounterId, docVersion]);

  const bmi = useMemo(() => {
    const w = num(v.weight_kg ?? ''), h = num(v.height_cm ?? '');
    return w && h ? Math.round((w / ((h / 100) ** 2)) * 10) / 10 : null;
  }, [v.weight_kg, v.height_cm]);

  async function addDiagnosis(d: { icd10_code?: string | null; label?: string | null }) {
    const next: DiagnosisRow[] = [...dx, { id: 'new-' + dx.length, icd10_code: d.icd10_code ?? null, label: d.label ?? null, is_primary: dx.length === 0 ? 1 : 0, certainty: 'confirmed', title_en: null, title_my: null }];
    setDx(next); setAddingDx(false);
    if (encounterId) { await saveDiagnoses(ctx, encounterId, next.map((x) => ({ icd10_code: x.icd10_code, label: x.label, is_primary: x.is_primary === 1, certainty: x.certainty as 'confirmed' }))); setDx(await loadDiagnoses(ctx, encounterId)); }
  }
  async function removeDiagnosis(id: string) {
    const next = dx.filter((x) => x.id !== id);
    setDx(next);
    if (encounterId) { await saveDiagnoses(ctx, encounterId, next.map((x) => ({ icd10_code: x.icd10_code, label: x.label, is_primary: x.is_primary === 1, certainty: x.certainty as 'confirmed' }))); setDx(await loadDiagnoses(ctx, encounterId)); }
  }
  async function persistRx(items: RxItemRow[]) {
    setRx(items);
    if (rxId) await saveRxItems(ctx, rxId, items);
  }

  const conflicts = useMemo(() => rx.map((r) => r.drug_name.toLowerCase()), [rx]);
  const [alerted, setAlerted] = useState<string[]>([]);
  useEffect(() => {
    if (!meta) return;
    void allergyConflicts(ctx, meta.patient_id, conflicts).then(setAlerted);
  }, [ctx, meta, conflicts]);

  async function sign() {
    if (!encounterId) return;
    await saveNarrative(ctx, encounterId, n, followUp || null);
    await signEncounter(ctx, encounterId, rxId);
    if (tokenId) await setTokenStatus({ ...ctx, tokenPrefix: '' }, tokenId, 'done');
    setMeta((m) => (m ? { ...m, status: 'signed' } : m));
    if (rx.length > 0) setPrinting(true);
    else nav('/queue');
  }

  if (!patient || !meta || !encounterId) return <div className="p-8 text-center text-muted">{t('common.loading')}</div>;
  const age = ageLabel(patient);
  const signed = meta.status === 'signed' || meta.status === 'amended';

  return (
    <div className="grid gap-4">
      <Card className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{patient.honorific && patient.honorific !== 'none' ? t(`honorific.${patient.honorific}`) + ' ' : ''}{my ? (patient.name_my ?? patient.name_en) : (patient.name_en ?? patient.name_my)}</h1>
          <div className="text-sm text-ink-2">
            {patient.patient_no}
            {age && <> · {age.years} {t('patient.ageYears')}{age.years < 5 ? ` ${age.months} ${t('patient.ageMonths')}` : ''}</>}
            {meta.doctor_name && <> · {meta.doctor_name}</>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {allergies.length > 0 && <Pill tone="danger">{t('patient.allergies')}: {allergies.join(', ')}</Pill>}
          {signed ? <Pill tone="accent">{t('consult.signed')}</Pill> : <span className="text-sm text-muted">{state === 'saving' ? t('consult.saving') : state === 'saved' ? t('consult.saved') : ''}</span>}
        </div>
      </Card>

      {alerted.length > 0 && <div className="rounded-xl border border-danger/40 bg-danger-soft px-4 py-3 font-semibold text-danger">⚠︎ {t('consult.allergyWarning', { names: alerted.join(', ') })}</div>}

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <Card className="grid gap-3 self-start">
          <h2 className="font-bold">{t('consult.vitals')}</h2>
          <div className="grid grid-cols-2 gap-2">
            <Field label={t('consult.bp')}>
              <div className="flex items-center gap-1">
                <Input inputMode="numeric" value={v.systolic ?? ''} onChange={(e) => setV({ ...v, systolic: e.target.value })} className="text-center" />
                <span className="text-muted">/</span>
                <Input inputMode="numeric" value={v.diastolic ?? ''} onChange={(e) => setV({ ...v, diastolic: e.target.value })} className="text-center" />
              </div>
            </Field>
            <Field label={t('consult.pulse')}><Input inputMode="numeric" value={v.pulse ?? ''} onChange={(e) => setV({ ...v, pulse: e.target.value })} /></Field>
            <Field label={t('consult.temp')}><Input inputMode="decimal" value={v.temp_c ?? ''} onChange={(e) => setV({ ...v, temp_c: e.target.value })} /></Field>
            <Field label={t('consult.spo2')}><Input inputMode="numeric" value={v.spo2 ?? ''} onChange={(e) => setV({ ...v, spo2: e.target.value })} /></Field>
            <Field label={t('consult.weight')}><Input inputMode="decimal" value={v.weight_kg ?? ''} onChange={(e) => setV({ ...v, weight_kg: e.target.value })} /></Field>
            <Field label={t('consult.height')}><Input inputMode="decimal" value={v.height_cm ?? ''} onChange={(e) => setV({ ...v, height_cm: e.target.value })} /></Field>
            <Field label={t('consult.glucose')}><Input inputMode="numeric" value={v.glucose_mg_dl ?? ''} onChange={(e) => setV({ ...v, glucose_mg_dl: e.target.value })} /></Field>
            <Field label={t('consult.bmi')}><Input readOnly value={bmi ?? ''} className="bg-bg" /></Field>
          </div>
        </Card>

        <div className="grid gap-4">
          <Card className="grid gap-3">
            <Field label={t('consult.complaint')}><Textarea className="min-h-16" value={n.chief_complaint ?? ''} onChange={(e) => setN({ ...n, chief_complaint: e.target.value })} autoFocus /></Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('consult.history')}><Textarea className="min-h-20" value={n.history ?? ''} onChange={(e) => setN({ ...n, history: e.target.value })} /></Field>
              <Field label={t('consult.examination')}><Textarea className="min-h-20" value={n.examination ?? ''} onChange={(e) => setN({ ...n, examination: e.target.value })} /></Field>
            </div>
          </Card>

          <Card className="grid gap-3">
            <div className="flex items-center justify-between"><h2 className="font-bold">{t('consult.diagnosis')}</h2><Button className="py-1" onClick={() => setAddingDx(true)}>＋ {t('consult.addDiagnosis')}</Button></div>
            {dx.length === 0 ? <p className="m-0 text-muted">—</p> : (
              <ul className="flex flex-wrap gap-2">
                {dx.map((d) => (
                  <li key={d.id}>
                    <Pill tone={d.is_primary ? 'accent' : 'neutral'}>
                      {d.icd10_code ? <span className="font-mono text-xs">{d.icd10_code}</span> : null} {my ? (d.title_my ?? d.label ?? d.title_en) : (d.title_en ?? d.label)}
                      <button className="ml-1.5 opacity-70 hover:opacity-100" onClick={() => removeDiagnosis(d.id)} aria-label="remove">✕</button>
                    </Pill>
                  </li>
                ))}
              </ul>
            )}
            <Field label={t('consult.plan')}><Textarea className="min-h-16" value={n.plan ?? ''} onChange={(e) => setN({ ...n, plan: e.target.value })} /></Field>
          </Card>

          <Card className="grid gap-3">
            <div className="flex items-center justify-between"><h2 className="font-bold">{t('consult.prescription')}</h2><Button className="py-1" variant="primary" onClick={() => setAddingRx({ free: '' })}>＋ {t('consult.addMedicine')}</Button></div>
            {rx.length === 0 ? <p className="m-0 text-muted">—</p> : (
              <ul className="divide-y divide-rule">
                {rx.map((r, i) => (
                  <li key={r.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2">
                    <div>
                      <div className="font-semibold">{r.drug_name} {r.buy_outside && <Pill tone="warn">{t('consult.buyOutside')}</Pill>}</div>
                      <div className="text-sm text-ink-2">
                        {r.dose_qty} {r.dose_unit} · {r.frequency_code} · {r.duration_days} {t('consult.duration')} · {t('consult.quantity')} {r.quantity}
                        {r.free_text ? ` · ${r.free_text}` : ''}
                      </div>
                    </div>
                    <button className="text-ink-2 hover:text-danger" onClick={() => persistRx(rx.filter((_, j) => j !== i))} aria-label="remove">✕</button>
                  </li>
                ))}
              </ul>
            )}
            {docs.length > 0 && (
              <ul className="flex flex-wrap gap-2">
                {docs.map((d) => (
                  <li key={d.id}>
                    <button className="rounded-full border border-rule bg-surface px-3 py-1 text-sm hover:bg-accent-soft" onClick={() => setShowDoc(d)}>
                      {t(`doc.${d.kind === 'medical_certificate' ? 'certificate' : d.kind}`)} · {d.doc_no}
                      {d.rest_days ? ` · ${t('doc.days', { count: d.rest_days })}` : ''}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('consult.followUp')}><Input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} min={new Date().toISOString().slice(0, 10)} /></Field>
              <div className="flex flex-wrap items-end gap-2">
                <Button onClick={() => setIssuingDoc(true)}>{t('doc.issue')}</Button>
                {rx.length > 0 && <Button onClick={() => setPrinting(true)}>{t('consult.printRx')}</Button>}
                {!signed && <Button variant="primary" className="flex-1 text-lg" onClick={sign}>{t('consult.sign')}</Button>}
                {signed && <Button variant="primary" className="flex-1" onClick={() => nav('/queue')}>{t('nav.queue')}</Button>}
              </div>
            </div>
          </Card>
        </div>
      </div>

      <Modal open={addingDx} onClose={() => setAddingDx(false)} title={t('consult.addDiagnosis')}>
        <SearchPicker<Icd10Hit>
          autoFocus
          placeholder={t('consult.searchDiagnosis')}
          search={icd10}
          render={(x) => ({ key: x.code, primary: (my ? (x.title_my ?? x.title_en) : x.title_en) ?? x.code, secondary: x.code })}
          onPick={(x) => addDiagnosis({ icd10_code: x.code, label: null })}
          onFreeText={(text) => addDiagnosis({ label: text })}
        />
      </Modal>

      <Modal open={!!addingRx} onClose={() => setAddingRx(null)} title={t('consult.addMedicine')} wide>
        {addingRx && <AddMedicine drugs={drugs} onCancel={() => setAddingRx(null)} onAdd={(item) => { void persistRx([...rx, { ...item, id: 'new-' + rx.length, sort_order: rx.length }]); setAddingRx(null); }} />}
      </Modal>

      <Modal open={issuingDoc} onClose={() => setIssuingDoc(false)} title={t('doc.issue')} wide>
        <IssueCertificate
          patientId={meta.patient_id} encounterId={encounterId} doctorId={meta.doctor_id}
          diagnosisText={dx.map((d) => (my ? (d.title_my ?? d.label ?? d.title_en) : (d.title_en ?? d.label))).filter(Boolean).join(', ') || null}
          prefix={clinic?.doc_no_prefix ?? 'MC'}
          onCancel={() => setIssuingDoc(false)}
          onIssued={async (id) => { setIssuingDoc(false); setDocVersion((v) => v + 1); setShowDoc(await getDocument(ctx, id)); }}
        />
      </Modal>

      <Modal open={!!showDoc} onClose={() => setShowDoc(null)} title={showDoc ? t(`doc.${showDoc.kind === 'medical_certificate' ? 'certificate' : showDoc.kind}`) : ''} wide>
        {showDoc && (
          <CertificatePrint
            doc={showDoc} patient={patient} doctorName={meta.doctor_name} doctorRegNo={meta.doctor_reg_no}
            clinicName={(my && clinic?.name_my) || clinic?.name || ''} clinicNameAlt={my ? clinic?.name ?? null : clinic?.name_my ?? null}
            clinicPhone={clinic?.phone ?? null} clinicAddress={clinic?.address_line ?? null}
            onPrinted={() => void countPrint(ctx, showDoc.id)}
            onClose={() => setShowDoc(null)}
          />
        )}
      </Modal>

      <Modal open={printing} onClose={() => { setPrinting(false); if (meta.status !== 'open') nav('/queue'); }} title={t('consult.prescription')} wide>
        <PrescriptionPrint
          patient={patient} items={rx} diagnoses={dx} doctorName={meta.doctor_name} followUp={followUp}
          clinicName={(my && clinic?.name_my) || clinic?.name || ''} clinicPhone={clinic?.phone ?? null}
          onClose={() => { setPrinting(false); if (meta.status !== 'open') nav('/queue'); }}
        />
      </Modal>
    </div>
  );
}

function AddMedicine({ drugs, onAdd, onCancel }: { drugs: (q: string) => Promise<DrugHit[]>; onAdd: (i: Omit<RxItemRow, 'id' | 'sort_order'>) => void; onCancel: () => void }) {
  const { t, i18n } = useTranslation();
  const my = i18n.language === 'my';
  const freqs = useDosagePhrases('frequency');
  const units = useDosagePhrases('dose_unit');
  const instructions = useDosagePhrases('instruction');
  const [drug, setDrug] = useState<DrugHit | null>(null);
  const [free, setFree] = useState('');
  const [f, setF] = useState({ dose_qty: '1', dose_unit: 'tab', frequency_code: 'TDS', duration_days: '5', quantity: '', instruction_codes: [] as string[], free_text: '', buy_outside: false });

  const times = freqs.find((x) => x.code === f.frequency_code)?.times_per_day ?? null;
  const auto = computeQuantity(Number(f.dose_qty) || null, times, Number(f.duration_days) || null);
  const quantity = f.quantity.trim() ? Number(f.quantity) : (auto ?? 0);
  const name = drug ? [drug.generic_name, drug.strength, drug.brand_name ? `(${drug.brand_name})` : ''].filter(Boolean).join(' ') : free;

  if (!drug && !free) {
    return (
      <SearchPicker<DrugHit>
        autoFocus
        placeholder={t('consult.searchMedicine')}
        search={drugs}
        render={(d) => ({
          key: d.id,
          primary: [d.generic_name, d.strength].filter(Boolean).join(' ') + (d.brand_name ? ` · ${d.brand_name}` : ''),
          secondary: d.qty_on_hand > 0 ? t('consult.inStock', { n: d.qty_on_hand }) : t('consult.outOfStock'),
          tone: d.qty_on_hand > 0 ? 'ok' : 'warn',
        })}
        onPick={(d) => { setDrug(d); setF({ ...f, dose_unit: d.dispense_unit === 'tablet' ? 'tab' : f.dose_unit, buy_outside: d.qty_on_hand <= 0 }); }}
        onFreeText={(text) => setFree(text)}
      />
    );
  }

  return (
    <div className="grid gap-3">
      <div className="flex items-baseline justify-between gap-2">
        <b>{name}</b>
        {drug && <span className={cx('text-sm', drug.qty_on_hand > 0 ? 'text-ink-2' : 'text-danger')}>{drug.qty_on_hand > 0 ? t('consult.inStock', { n: drug.qty_on_hand }) : t('consult.outOfStock')}</span>}
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Field label={t('consult.dose')}><Input inputMode="decimal" value={f.dose_qty} onChange={(e) => setF({ ...f, dose_qty: e.target.value })} autoFocus /></Field>
        <Field label=" ">
          <select className="w-full min-h-11 rounded-lg border border-rule bg-surface px-3" value={f.dose_unit} onChange={(e) => setF({ ...f, dose_unit: e.target.value })}>
            {units.map((u) => <option key={u.code} value={u.code}>{my ? u.text_my : u.text_en}</option>)}
          </select>
        </Field>
        <Field label={t('consult.frequency')}>
          <select className="w-full min-h-11 rounded-lg border border-rule bg-surface px-3" value={f.frequency_code} onChange={(e) => setF({ ...f, frequency_code: e.target.value })}>
            {freqs.map((x) => <option key={x.code} value={x.code}>{my ? x.text_my : x.text_en}</option>)}
          </select>
        </Field>
        <Field label={t('consult.duration')}><Input inputMode="numeric" value={f.duration_days} onChange={(e) => setF({ ...f, duration_days: e.target.value })} /></Field>
      </div>
      <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
        <Field label={t('consult.instructions')}>
          <div className="flex flex-wrap gap-1.5">
            {instructions.map((x) => (
              <button type="button" key={x.code}
                onClick={() => setF({ ...f, instruction_codes: f.instruction_codes.includes(x.code) ? f.instruction_codes.filter((c) => c !== x.code) : [...f.instruction_codes, x.code] })}
                className={cx('rounded-full border px-3 py-1.5 text-sm font-semibold', f.instruction_codes.includes(x.code) ? 'border-accent bg-accent-soft text-accent-ink' : 'border-rule bg-surface text-ink-2')}>
                {my ? x.text_my : x.text_en}
              </button>
            ))}
          </div>
        </Field>
        <Field label={t('consult.quantity')}><Input inputMode="numeric" value={f.quantity || String(auto ?? '')} onChange={(e) => setF({ ...f, quantity: e.target.value })} className="w-24 text-center text-lg font-bold" /></Field>
      </div>
      <Field label={t('consult.free')}><Input value={f.free_text} onChange={(e) => setF({ ...f, free_text: e.target.value })} /></Field>
      <label className="flex items-center gap-2"><input type="checkbox" className="h-5 w-5" checked={f.buy_outside} onChange={(e) => setF({ ...f, buy_outside: e.target.checked })} />{t('consult.buyOutside')}</label>
      <div className="flex justify-end gap-2">
        <Button onClick={onCancel}>{t('common.cancel')}</Button>
        <Button variant="primary" disabled={!name || quantity <= 0} onClick={() => onAdd({
          drug_id: drug?.id ?? null, drug_name: name, dose_qty: Number(f.dose_qty) || null, dose_unit: f.dose_unit,
          frequency_code: f.frequency_code, route_code: null, instruction_codes: f.instruction_codes,
          duration_days: Number(f.duration_days) || null, quantity, buy_outside: f.buy_outside, free_text: f.free_text || null,
        })}>{t('common.save')}</Button>
      </div>
    </div>
  );
}
